import type { Analytics } from "firebase/analytics";

// GA4 caps event parameter values at 100 characters.
const PARAM_MAX = 100;

interface ExceptionEvent {
  description: string;
  fatal: boolean;
  error_location?: string;
}

let analytics: Analytics | null = null;
let pending: ExceptionEvent[] | null = [];

export function initCrashReporter(): void {
  const env = import.meta.env;
  if (!env.VITE_FIREBASE_API_KEY || !env.VITE_FIREBASE_APP_ID) {
    pending = null;
    return;
  }

  window.addEventListener("error", (event) => {
    const location = event.filename
      ? `${event.filename}:${event.lineno}:${event.colno}`
      : undefined;
    reportError(event.error ?? event.message, { fatal: true, location });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, { fatal: true });
  });

  void loadAnalytics(env).catch(() => {
    pending = null;
  });
}

export function reportError(
  error: unknown,
  opts: { fatal?: boolean; location?: string } = {},
): void {
  if (!analytics && !pending) return;
  const event: ExceptionEvent = {
    description: truncate(describe(error)),
    fatal: opts.fatal ?? false,
  };
  const location = opts.location ?? stackTop(error);
  if (location) event.error_location = truncate(location);
  if (analytics) void send(analytics, event);
  else pending?.push(event);
}

async function loadAnalytics(env: ImportMetaEnv): Promise<void> {
  const [{ initializeApp }, { getAnalytics, isSupported }] = await Promise.all([
    import("firebase/app"),
    import("firebase/analytics"),
  ]);
  if (!(await isSupported())) {
    pending = null;
    return;
  }
  const app = initializeApp({
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
    measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
  });
  analytics = getAnalytics(app);
  const queued = pending ?? [];
  pending = null;
  for (const event of queued) void send(analytics, event);
}

async function send(target: Analytics, event: ExceptionEvent): Promise<void> {
  const { logEvent } = await import("firebase/analytics");
  logEvent(target, "exception", { ...event });
}

function describe(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

function stackTop(error: unknown): string | undefined {
  if (!(error instanceof Error) || !error.stack) return undefined;
  const frame = error.stack
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.startsWith("at ") || line.includes("@"));
  return frame?.replace(/^at /, "");
}

function truncate(text: string): string {
  return text.length > PARAM_MAX ? `${text.slice(0, PARAM_MAX - 1)}…` : text;
}
